// @givmo/design-system — SandboxedHtmlPreview (iframe + CSP + DOMPurify, defense-in-depth).
//
// 03-3d. Renders a STATIC HTML preview inside a hardened <iframe sandbox=…> for the ONE
// sanctioned caller: super-admin's legal/email-template preview. The HTML it renders is
// admin-curated legal/marketing/email markup — NEVER model-authored HTML. The assistant
// never renders model HTML; that is a hard trust posture, not a config toggle. This
// component carries no escape hatch that would let it.
//
// THREE independent layers neutralise hostile markup (none alone is trusted):
//   1. SANITIZE — DOMPurify strips <script>, inline event handlers (onerror/onclick/…),
//      javascript:/data: URLs, <object>/<embed>/<iframe>, <form>, etc., BEFORE the markup
//      ever reaches the DOM. This is the primary defence; the iframe/CSP are backstops.
//   2. SANDBOX — the markup is rendered in an <iframe> with the MINIMAL sandbox token set.
//      We grant NOTHING by default (empty `sandbox=""` → unique opaque origin, scripts
//      disabled, forms disabled, top-navigation disabled, popups disabled, no same-origin).
//      A static preview needs no `allow-scripts` and MUST NOT combine `allow-same-origin`
//      with `allow-scripts` (that pairing lets framed script remove its own sandbox).
//   3. CSP — a strict <meta http-equiv="Content-Security-Policy"> inside the framed
//      document blocks script execution, object/embed, and ALL external/network loads
//      (`default-src 'none'`), so even a sanitiser bypass cannot exfiltrate or run code.
//
// Delivery is via `srcdoc` (not a blob: URL): the sanitised string is inlined into the
// frame, the frame stays same-document for sizing, and there is no object-URL lifecycle to
// leak. Because `sandbox` has no `allow-same-origin`, the srcdoc document is itself a unique
// opaque origin regardless.
//
// a11y: the <iframe> carries a required `title`; the outer region is `aria-label`-able.
// `testId` forwards to the <iframe>. The component is purely presentational (no controls).
import { useMemo } from 'react';
import DOMPurify, { type Config } from 'dompurify';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

/**
 * The strict Content-Security-Policy injected into the framed document. `default-src 'none'`
 * denies every fetch directive that isn't separately re-allowed; we re-allow only inline
 * styles and same-document images so legal/email HTML still renders. Crucially `script-src`,
 * `object-src`, `frame-src`, `connect-src` stay denied — no script execution, no plugins,
 * no network. This string is the in-frame backstop behind DOMPurify.
 */
const FRAME_CSP = [
  "default-src 'none'",
  'img-src data:',
  "style-src 'unsafe-inline'",
  'font-src data:',
  "base-uri 'none'",
  "form-action 'none'",
].join('; ');

/**
 * DOMPurify configuration. We keep the default (safe) allow-list and additionally forbid the
 * embedding/scripting tags outright and strip ALL `target` attributes so nothing can break out
 * of the frame. `FORBID_TAGS`/`FORBID_ATTR` are belt-and-suspenders over DOMPurify's defaults
 * (which already drop <script> and on* handlers).
 *
 * URI policy — read carefully, the two `data:` cases are NOT the same:
 *   - `javascript:` and `data:text/html`-style *document* URIs (anything that could be a
 *     navigable/executable document) are stripped by `ALLOWED_URI_REGEXP` below, which only
 *     admits http(s)/mailto/tel schemes plus relative URLs.
 *   - `data:image/*` on `<img src>` is governed by DOMPurify's SEPARATE `data:`-URI allow-list
 *     for image tags (not by `ALLOWED_URI_REGEXP`), and is INTENTIONALLY permitted: legal/email
 *     templates routinely inline raster logos/seals as `data:image/png|jpeg|gif|webp`. Such an
 *     `<img>` is rendered in static image mode (no script, no onload script execution), and even
 *     a hostile data-image cannot run code or egress because `default-src 'none'` + the empty
 *     `sandbox=""` independently forbid execution and network. We additionally DROP
 *     `data:image/svg+xml` (raster-only residual): SVG-as-data-image is not needed for these
 *     previews and is the one image-data type that can carry markup, so the `uponSanitizeAttribute`
 *     hook below removes it while letting raster data-images through.
 *   - http(s) `<img src>` and CSS `url(http://…)` in inline `style` ALSO pass DOMPurify (they are
 *     valid markup); their network egress is contained by the CSP (`img-src data:` only —
 *     NOT `*`/`https:` — and `default-src 'none'` for everything else), NOT by sanitisation. The
 *     XSS test locks that CSP posture so a future loosening trips it.
 */
const SANITIZE_CONFIG: Config = {
  FORBID_TAGS: ['script', 'object', 'embed', 'iframe', 'form', 'base', 'meta', 'link'],
  FORBID_ATTR: ['target', 'srcdoc', 'formaction'],
  // Admit only http(s)/mailto/tel + relative URLs for navigable/href-like attributes; this kills
  // `javascript:` and `data:text/html` document URIs. (Image `data:` URIs are handled separately
  // by DOMPurify's image data-URI allow-list — see the block comment above.)
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

// Raster-only residual: drop `data:image/svg+xml` on image src/href so the one markup-bearing
// image-data type can't slip through, while raster `data:image/png|jpeg|gif|webp` stays. (PNG/JPEG
// etc. render inertly; SVG is dropped at the sanitiser — defence in depth behind CSP + sandbox.)
let sanitizeHookInstalled = false;

function ensureSanitizeHooks() {
  if (sanitizeHookInstalled) return;

  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (
      (data.attrName === 'src' || data.attrName === 'xlink:href' || data.attrName === 'href') &&
      /^\s*data:image\/svg\+xml/i.test(data.attrValue)
    ) {
      data.keepAttr = false;
    }
  });
  sanitizeHookInstalled = true;
}

/**
 * Sanitise raw HTML and wrap it in a minimal document that carries the strict CSP meta. The
 * returned string is what goes into the iframe `srcdoc`. Exported for the test + any caller
 * that wants the exact sanitised payload (e.g. to log what was stripped).
 */
export function buildSandboxedDocument(html: string): string {
  ensureSanitizeHooks();
  const clean = DOMPurify.sanitize(html, SANITIZE_CONFIG);
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}"></head><body>${clean}</body></html>`;
}

export interface SandboxedHtmlPreviewProps extends TestIdProps {
  /**
   * Admin-curated legal/email HTML to preview. NOT for model-authored HTML — this component
   * exists only for the trusted super-admin preview surface.
   */
  html: string;
  /**
   * Required accessible title for the <iframe> (e.g. "Donation receipt email preview").
   * An iframe with no title is an a11y failure, so this is non-optional.
   */
  title: string;
  /** Height of the preview frame in px (default 480). Width fills the container. */
  height?: number;
  /** Extra classes merged onto the outer wrapper. */
  className?: string;
}

/**
 * Sandboxed, CSP-hardened, DOMPurify-sanitised static HTML preview. See the file header for
 * the three-layer defence rationale. Renders an <iframe sandbox> with no privileges granted.
 */
export function SandboxedHtmlPreview({
  html,
  title,
  height = 480,
  className,
  testId,
}: SandboxedHtmlPreviewProps) {
  // Sanitise + wrap once per `html` change. Memoised so re-renders don't re-run DOMPurify.
  const srcDoc = useMemo(() => buildSandboxedDocument(html), [html]);

  return (
    <div className={cn('overflow-hidden rounded-all12 border border-gray40 bg-white', className)}>
      <iframe
        title={title}
        // Empty sandbox = the most restrictive frame possible: unique opaque origin, no
        // scripts, no forms, no top-navigation, no popups, no same-origin. We intentionally
        // grant NOTHING — a static preview needs nothing, and `allow-scripts` +
        // `allow-same-origin` together would defeat the sandbox.
        sandbox=""
        srcDoc={srcDoc}
        // No referrer leaks out of the frame.
        referrerPolicy="no-referrer"
        className="block h-full w-full border-0 bg-white"
        style={{ height }}
        {...testIdProp(testId)}
      />
    </div>
  );
}
